import {
  FormFieldConfig,
  FormFieldInputType, FormFieldKey,
  FormFieldType,
  FormFieldValidator, FormFieldValueType,
} from 'src/app/@shared/form/form.types';
import { ValidatorFn, Validators } from '@angular/forms';
import { transformToUpperSnakeCase } from 'src/app/@shared/utils/to-upper-snake-case';

type FormFieldOption = {
  id: string;
  label: string;
  value?: number | string; // set for type 'select'
  disabled?: boolean; // set for disabled select options
  placeholder?: string; // set for type 'dynamic'
};

export class FormFieldModel {
  public type: FormFieldType;
  public inputType: FormFieldInputType;
  public isHidden: boolean;
  public isRequired: boolean;
  public isReadOnly: boolean;
  public id: FormFieldKey;
  public parentId: FormFieldKey; // set for fields of dynamic fields
  public label: string;
  public placeholder: string;
  public validators: ValidatorFn[] = [];
  public hasMultipleValues: boolean;
  public isNotPublic?: boolean; // will be set dynamically when needed (e.g. in the organization-form)

  public options: FormFieldOption[] = []; // set for type = 'select'
  public errorText?: string;
  public hintText?: string;
  public acceptedFileTypes?: string;
  public hasTranslation: boolean;

  public valueType: FormFieldValueType = 'default'; // helps to determine the output format of the value (e.g. on ProtocolPage)

  constructor(key: FormFieldKey, source: FormFieldConfig, parentKey?: FormFieldKey) {
    this.id = key;
    this.parentId = parentKey;
    this.type = source.type;
    this.inputType = source.inputType || 'text';
    this.isHidden = source.isHidden;
    this.validators = this.getParsedValidators(source.validators);
    this.isRequired = this.getIsRequired(source.validators);
    this.isReadOnly = source.readOnly;
    this.hasMultipleValues = source.hasMultipleValues;
    this.acceptedFileTypes = source.acceptedFileTypes || ''; // all file types accepted if empty string
    this.hasTranslation = source.hasTranslation;

    const translateKeyBase = transformToUpperSnakeCase(source.category || this.id);

    const optionsTranslateKey = source.optionsCategory
      ? transformToUpperSnakeCase(source.optionsCategory)
      : translateKeyBase;

    this.label = `FORM.INPUT.${translateKeyBase}.LABEL`;

    if (source.hasCustomErrorText) {
      this.errorText = `FORM.INPUT.${translateKeyBase}.ERROR_TEXT`;
    }

    if (source.hasHint) {
      this.hintText = `FORM.INPUT.${translateKeyBase}.HINT_TEXT`;
    }

    switch (source.type) {
      case 'select':
        this.setSelectField(
          key, optionsTranslateKey, source.options, source.disabledOptions, source.lockedOptions,
        );
        break;

      case 'selectModal':
        this.setSelectModalField();
        break;

      case 'dynamic':
        this.setDynamicField(key, source, translateKeyBase);
        break;

      default:
        this.setSimpleField(key, translateKeyBase);
    }

    this.setValueType();
  }

  protected setDynamicField(key: FormFieldKey, source: FormFieldConfig, translateKey: string): void {
    console.warn('override setDynamicField', key, source, translateKey);
  }

  protected setSimpleField(_key: FormFieldKey, translateKey: string): void {
    if (!this.isReadOnly) {
      this.placeholder = `FORM.INPUT.${translateKey}.PLACEHOLDER`;
    }
    // any simple field exclusive stuff?
  }

  protected setValueType(): void {
    if (this.hasTranslation || this.inputType === 'toggle') {
      this.valueType = 'translatable';

      return;
    }

    if (this.type === 'selectModal') {
      this.valueType = 'entityArray';

      return;
    }

    switch (this.inputType) {
      case 'date':
        this.valueType = 'date';
        break;

      case 'file':
        this.valueType = 'file';
        break;

      default:
        this.valueType = 'default';
    }
  }

  private setSelectModalField(): void {
    this.placeholder = 'FORM.COMMON.PLACEHOLDER.SELECT';
  }

  private setSelectField(
    _key: FormFieldKey,
    translateKey: string,
    options: readonly (string | number)[],
    disabledOptions: readonly (string | number)[] = [],
    lockedOptions: readonly (string | number)[] = [],
  ): void {
    const disabledOptionValues = new Set([...disabledOptions, ...lockedOptions]);

    this.placeholder = 'FORM.COMMON.PLACEHOLDER.SELECT';
    this.options = (options || []).map((value) => {
      let label: string;

      if (this.hasTranslation && typeof value === 'string') {
        label = `FORM.INPUT.${translateKey}.OPTIONS.${transformToUpperSnakeCase(value)}`;
      } else {
        label = value.toString();
      }

      return {
        disabled: disabledOptionValues.has(value),
        id: translateKey + value,
        label,
        value,
      };
    });
  }

  private getIsRequired(validators: FormFieldValidator[]): boolean {
    return !!validators?.filter((v) => { return v.key === 'required' || v.key === 'requiredTrue'; }).length;
  }

  private getParsedValidators(validators: FormFieldValidator[]): ValidatorFn[] {
    if (!validators) {
      return [];
    }

    return validators.map((validator) => {
      /* eslint-disable padding-line-between-statements */
      switch (validator.key) {
        case 'min':
        case 'minLength':
        case 'max':
        case 'maxLength':
          return Validators[validator.key](validator.value as number);
        case 'pattern':
          return Validators[validator.key](validator.value as string | RegExp);

        default:
          return Validators[validator.key];
      }
    });
  }
}
